import React, { useEffect, useState } from 'react';
import './EntryPackPicker.css';
import ENTRY_PACK_DESCRIPTIONS from '../../config/entryPacks';
import { getEntryPacks } from '../../api/testSystem';
import {
    PACKS_UNAVAILABLE_REASON,
    cheapestPerEntryId,
    entriesLabel,
    formatPackMoney,
    normalizeEntryPacks,
} from '../../lib/entryPacks';

/**
 * The packs on sale, read once per page. A failed read is treated like "Stripe
 * off": no packs. The pages then show nothing about buying at all — they key on
 * `packs` being empty, and do not print `reason`.
 */
export const useEntryPacks = (enabled = true) => {
    const [state, setState] = useState({ loading: true, packs: [], reason: '' });

    useEffect(() => {
        if (!enabled) return undefined;
        let alive = true;

        getEntryPacks()
            .then((data) => {
                if (alive) setState({ loading: false, ...normalizeEntryPacks(data) });
            })
            .catch(() => {
                if (alive) setState({ loading: false, packs: [], reason: PACKS_UNAVAILABLE_REASON });
            });

        return () => { alive = false; };
    }, [enabled]);

    return state;
};

/**
 * One card per pack — entries, price, price per entry and the tax line — picked
 * like a radio button. `layout="stack"` is the narrow column on a cycle page;
 * the default lays the packs side by side.
 */
const EntryPackPicker = ({
    packs,
    selected,
    onSelect,
    disabled = false,
    name,
    label,
    layout = 'row',
    showDescriptions = false,
    autoFocus = false,
}) => {
    const bestId = cheapestPerEntryId(packs);

    return (
        <div
            className={`ts-packs ts-packs--${layout}`}
            role="radiogroup"
            aria-label={label}
            data-testid="entry-packs"
        >
            {packs.map((pack) => {
                const on = pack.productId === selected;
                const description = showDescriptions ? ENTRY_PACK_DESCRIPTIONS[pack.productId] : '';

                return (
                    <label
                        className={`ts-pack${on ? ' ts-pack--on' : ''}${disabled ? ' ts-pack--disabled' : ''}`}
                        key={pack.productId}
                        data-testid="entry-pack"
                    >
                        <input
                            className="ts-pack-radio"
                            type="radio"
                            name={name}
                            value={pack.productId}
                            checked={on}
                            disabled={disabled}
                            autoFocus={autoFocus && on}
                            onChange={() => onSelect(pack.productId)}
                            data-testid={`pack-${pack.productId}`}
                        />
                        <span className="ts-pack-entries">{entriesLabel(pack.entries)}</span>
                        <span className="ts-pack-money">
                            <span className="ts-pack-price" data-testid="pack-price">
                                {formatPackMoney(pack.amount, pack.currency)}
                            </span>
                            <span className="ts-pack-tax">{pack.taxLabel}</span>
                        </span>
                        <span className="ts-pack-per" data-testid="pack-per-entry">
                            {formatPackMoney(pack.perEntryAmount, pack.currency)} per entry
                        </span>
                        {pack.productId === bestId && (
                            <span className="ts-pack-best" data-testid="pack-best">Lowest price per entry</span>
                        )}
                        {description && <span className="ts-pack-desc">{description}</span>}
                    </label>
                );
            })}
        </div>
    );
};

export default EntryPackPicker;
